import { A11yModule, LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { UserService } from '@core/user.service';
import { SchoolLicense } from '@models/license/school-license';
import { NgbActiveModal, NgbModal, NgbModalRef, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ButtonComponent } from '@shared/button/button.component';
import { LicenseStatus } from '@shared/enums/license-status';
import { RoleType } from '@shared/enums/role-type';
import { FeedbackData } from '@shared/feedback-form-modal/feedback-data';
import {
  assetFeedbackOption,
  productSpecificFeedbackOption,
  userFeedbackOptions
} from '@shared/feedback-form-modal/feedback-options';
import { HeaderService } from '@shared/header/header-service';
import { ModalTemplateComponent } from '@shared/modals/modal-template/modal-template.component';
import { ModalTemplateNoHeaderParams } from '@shared/modals/modal-template-no-header/modal-template-no-header-params';
import { ModalTemplateNoHeaderComponent } from '@shared/modals/modal-template-no-header/modal-template-no-header.component';
import { UnsavedChangesModalComponent } from '@shared/modals/unsaved-changes/unsaved-changes-modal.component';
import { SelectComponent } from '@shared/select/select.component';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'zbp-feedback-form-modal',
  templateUrl: './feedback-form-modal.component.html',
  styleUrls: ['./feedback-form-modal.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    ButtonComponent,
    NgbTooltipModule,
    SelectComponent,
    ReactiveFormsModule,
    ModalTemplateComponent,
    A11yModule
  ]
})
export class FeedbackFormModalComponent implements OnInit, OnDestroy {
  protected readonly zbpIconNames = zbpIconNames;

  readonly optionFormControl: FormControl<string> = new FormControl<string>(
    null, Validators.required
  );
  readonly productFormControl: FormControl<SchoolLicense> = new FormControl<SchoolLicense>(
    null, Validators.required
  );
  readonly noteFormControl: FormControl<string> = new FormControl<string>(
    null, [Validators.required, Validators.minLength(2)]
  );

  readonly formGroup = new FormGroup({
    option: this.optionFormControl,
    product: this.productFormControl,
    note: this.noteFormControl,
  });

  logoSrc = 'assets/zbportal/img/zb-logo-red.svg';
  logoAlt = 'ZB Portal logo';
  feedbackOptions = userFeedbackOptions;
  maxLength = 1000;
  savingFeedback: boolean = false;
  productOptions: SchoolLicense[] = [];
  educationalUnitId: string;
  isMaterialViewer: boolean = false;

  subscriptions: Subscription[] = [];

  SUCCESS_MESSAGE: string = `Your feedback has been successfully submitted to Zaner-Bloser.<br><br>Thank you!`;
  FAILURE_MESSAGE: string = 'Unable to save your input, try again later.';

  thumbsUpSelected = signal<boolean>(false);
  thumbsDownSelected = signal<boolean>(false);

  feedbackNote = toSignal(this.noteFormControl.valueChanges, { initialValue: this.noteFormControl.value });

  constructor(public activeModal: NgbActiveModal,
              private modalService: NgbModal,
              public liveAnnouncer: LiveAnnouncer,
              private userService: UserService,
              private headerService: HeaderService,
              private titleService: Title,
              private router: Router,) {
  }

  ngOnInit() {
    if (this.isMaterialViewer) {
      this.optionFormControl.setValue(assetFeedbackOption);
      this.optionFormControl.disable();
      this.optionFormControl.updateValueAndValidity();
      this.formGroup.removeControl('product');

      const school = this.userService.user.schools?.[0];
      this.educationalUnitId = school?.educationalUnitId ?? '';

      return;
    }

    this.subscriptions.push(
      this.optionFormControl.valueChanges.subscribe((value) => {
        if (value && value === productSpecificFeedbackOption) {
          this.formGroup.addControl('product', this.productFormControl);
        } else {
          this.formGroup.removeControl('product');
        }
      })
    );

    if (this.userService.user.isTeacher) {
      this.educationalUnitId = this.headerService.schoolId;
      this.getProducts(RoleType.Teacher);
    } else if (this.userService.user.isDistrictAdmin) {
      this.educationalUnitId = this.headerService.districtId;
      this.getProducts(RoleType.DistrictAdministrator);
    } else if (this.userService.user.isSchoolAdmin) {
      this.educationalUnitId = this.headerService.schoolId;
      this.getProducts(RoleType.SchoolAdministrator);
    } else {
      console.log('Feedback unavailable for role :: ', this.userService.user.viewingAsRole);
      this.activeModal.close();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  getProducts(role: RoleType) {
    this.subscriptions.push(
      this.headerService.getLicenses$
        .pipe(
          filter(licenses => !!licenses),
        )
        .subscribe((licenses) => {
          const assignedLicenses = licenses.filter(
            license => role !== RoleType.Teacher || license.status === LicenseStatus.AssignedToYou);

          const seen = new Set<string>();
          const uniqueLicenses: SchoolLicense[] = assignedLicenses
            .map((license) => {
              let description = license.description
                .replace(/\s*(Teacher|Class|Student)\s+License.*$/i, '')
                .trim();

              if (description.endsWith(',')) {
                description = description.slice(0, -1).trim();
              }

              return { ...license, description } as SchoolLicense;
            })
            .filter(p => !seen.has(p.description) && seen.add(p.description));

          this.productOptions.push(...uniqueLicenses);
        })
    );
  }

  updateThumbUpIcon() {
    this.thumbsUpSelected.set(!this.thumbsUpSelected());
    if (this.thumbsDownSelected()) {
      this.thumbsDownSelected.set(!this.thumbsUpSelected());
    }
    this.liveAnnouncer.announce(`Thumbs Up ${this.thumbsUpSelected() ? 'selected' : 'not selected'}`, 'polite');
  }

  updateThumbDownIcon() {
    this.thumbsDownSelected.set(!this.thumbsDownSelected());
    if (this.thumbsUpSelected()) {
      this.thumbsUpSelected.set(!this.thumbsDownSelected());
    }
    this.liveAnnouncer.announce(`Thumbs Down ${this.thumbsDownSelected() ? 'selected' : 'not selected'}`, 'polite');
  }

  public static open(ngbModal: NgbModal, fromMaterialViewer: boolean): NgbModalRef {
    const modalRef = ngbModal.open(
      FeedbackFormModalComponent,
      {
        size: 'lg',
        beforeDismiss: () => {
          if (!modalRef.componentInstance?.hasValues()) return true;
          return UnsavedChangesModalComponent.open(ngbModal).result.then(() => true, () => false);
        }
      },
    );

    const component = modalRef.componentInstance;
    component.isMaterialViewer = fromMaterialViewer;

    return modalRef;
  }

  hasValues(): boolean {
    const formValues = this.formGroup.value;
    return !!formValues.option || !!formValues.note;
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  confirm(): void {
    this.savingFeedback = true;
    const feedbackData = this.getFeedbackData();

    this.subscriptions.push(
      this.userService.submitFeedback(feedbackData)
        .subscribe((result) => {
          if (result.success) {
            this.openSuccessModal();
          } else {
            this.openFailureModal();
          }
          this.savingFeedback = false;
        })
    );
  }

  getFeedbackData(): FeedbackData {
    const product = this.productFormControl.value ? this.productFormControl.value : undefined;

    const feedbackData = {
      educationalUnitId: this.educationalUnitId,
      feedbackType: this.optionFormControl.value,
      url: this.router.url,
      pageName: this.titleService.getTitle(),
      reaction: this.getReaction(),
      message: this.noteFormControl.value,
    } as FeedbackData;

    if (product) {
      feedbackData.productType = this.productFormControl.value.productType;
      feedbackData.variantType = this.productFormControl.value.variant;
    }

    return feedbackData;
  }

  getReaction(): string {
    if (this.thumbsUpSelected()) {
      return 'thumbs-up';
    }

    if (this.thumbsDownSelected()) {
      return 'thumbs-down';
    }

    return 'none';
  }

  openSuccessModal() {
    const params: ModalTemplateNoHeaderParams = {
      body: this.SUCCESS_MESSAGE,
      confirm: {
        label: 'Close'
      },
    };

    // submission was successful - close the feedback modal
    this.activeModal.close(true);

    ModalTemplateNoHeaderComponent.open(this.modalService, params);
  }

  openFailureModal() {
    const params: ModalTemplateNoHeaderParams = {
      body: this.FAILURE_MESSAGE,
      confirm: {
        label: 'Close'
      },
    };

    ModalTemplateNoHeaderComponent.open(this.modalService, params);
  }

  get charCount(): number {
    return this.feedbackNote()?.length ?? 0;
  }

  protected readonly productSpecificFeedbackOption = productSpecificFeedbackOption;
}
